import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useAppSelector } from "../../store/hooks";
import { selectListingsWithDetails } from "../../store/slices/listingsSlice";
import "./ContactForm.css";

interface ContactFormProps {
  recipientName: string;
  recipientId: string;
  onClose: () => void;
}

export const ContactForm = ({
  recipientName,
  recipientId,
  onClose,
}: ContactFormProps) => {
  const { user } = useAuth();
  const listingsWithDetails = useAppSelector(selectListingsWithDetails);
  
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setSenderEmail(user.email);
      
      if (user.primaryListingId && listingsWithDetails[user.primaryListingId]) {
        const userListing = listingsWithDetails[user.primaryListingId];
        setSenderName(userListing.name || "");
      }
    }
  }, [user, listingsWithDetails]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!user) {
      setError("You must be logged in to send a message");
      return;
    }

    if (!senderName.trim() || !senderEmail.trim() || !subject.trim() || !message.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setSending(true);

    try {
      const response = await fetch("/api/contact-member", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          recipientMemberId: recipientId,
          senderName: senderName.trim(),
          senderEmail: senderEmail.trim(),
          subject: subject.trim(),
          message: message.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="contact-form-overlay" onClick={onClose}>
        <div className="contact-form-modal" onClick={(e) => e.stopPropagation()}>
          <button className="contact-form-close" onClick={onClose}>
            ×
          </button>
          <h2>Login Required</h2>
          <p>Please log in to send a message to {recipientName}.</p>
          <button className="contact-form-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="contact-form-overlay" onClick={onClose}>
        <div className="contact-form-modal" onClick={(e) => e.stopPropagation()}>
          <button className="contact-form-close" onClick={onClose}>
            ×
          </button>
          <h2>Message Sent!</h2>
          <p>Your message to {recipientName} has been sent successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-form-overlay" onClick={onClose}>
      <div className="contact-form-modal" onClick={(e) => e.stopPropagation()}>
        <button className="contact-form-close" onClick={onClose}>
          ×
        </button>
        <h2>Contact {recipientName}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="contact-form-field">
            <label htmlFor="senderName">Your Name</label>
            <input
              type="text"
              id="senderName"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Enter your name"
              required
            />
          </div>

          <div className="contact-form-field">
            <label htmlFor="senderEmail">Your Email</label>
            <input
              type="email"
              id="senderEmail"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="contact-form-field">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              required
            />
          </div>

          <div className="contact-form-field">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message..."
              rows={6}
              required
            />
          </div>

          {error && <div className="contact-form-error">{error}</div>}

          <div className="contact-form-actions">
            <button
              type="button"
              className="contact-form-button contact-form-button-secondary"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="contact-form-button contact-form-button-primary"
              disabled={sending}
            >
              {sending ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
